const SHOPIFY_DOMAIN = "elywine.myshopify.com";
const API_VERSION = "2025-07";
const ACCESS_TOKEN = "87def289fc27508c31535f7e4969e627";

type ShopifyFetchResponse<T> = T | null;

// Helper to send GraphQL queries/mutations to Shopify
async function shopifyFetch<T>(query: string, variables: Record<string, unknown> = {}): Promise<ShopifyFetchResponse<T>> {
  try {
    const res = await fetch(`https://${SHOPIFY_DOMAIN}/api/${API_VERSION}/graphql.json`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": ACCESS_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    });

    const json = await res.json();
    if (json.errors) console.error("Shopify errors:", json.errors);
    return json.data as T;
  } catch (err) {
    console.error("Shopify fetch error:", err);
    return null;
  }
}

type ProductVariant = {
  id: string;
  title: string;
  priceV2: { amount: string; currencyCode: string };
  product?: { title: string };
};

type Product = {
  id: string;
  title: string;
  description?: string;
  images: { src: string; altText?: string | null }[];
  variants: ProductVariant[];
};

// Fetch products from a collection
export async function fetchShopifyCollection(handle: string): Promise<Product[]> {
  const query = `
    query getCollection($handle: String!) {
      collectionByHandle(handle: $handle) {
        title
        products(first: 10) {
          edges {
            node {
              id
              title
              description
              images(first: 3) {
                edges {
                  node {
                    src
                    altText
                  }
                }
              }
              variants(first: 5) {
                edges {
                  node {
                    id
                    title
                    priceV2 {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  type QueryResult = {
    collectionByHandle: {
      products: { edges: { node: Omit<Product, "images" | "variants"> & {
        images: { edges: { node: Product["images"][number] }[] };
        variants: { edges: { node: ProductVariant }[] };
      } }[] };
    } | null;
  };

  const data = await shopifyFetch<QueryResult>(query, { handle });

  if (!data?.collectionByHandle) {
    console.warn(`Collection "${handle}" not found or empty.`);
    return [];
  }

  return data.collectionByHandle.products.edges.map(edge => ({
    ...edge.node,
    images: edge.node.images.edges.map(e => e.node),
    variants: edge.node.variants.edges.map(e => e.node),
  }));
}

// Create a new cart
export async function createCart() {
  const query = `
    mutation {
      cartCreate {
        cart {
          id
          checkoutUrl
        }
      }
    }
  `;
  type MutationResult = { cartCreate?: { cart?: { id: string; checkoutUrl: string } } };
  const data = await shopifyFetch<MutationResult>(query);
  return data?.cartCreate?.cart || null;
}

// Add product variant to cart
export async function addToCart(cartId: string, variantId: string, quantity = 1) {
  const query = `
    mutation addToCart($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          id
          checkoutUrl
          totalQuantity
          lines(first: 10) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    product {
                      title
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const variables = {
    cartId,
    lines: [{ merchandiseId: variantId, quantity }],
  };

  type MutationResult = { 
    cartLinesAdd?: { 
      cart?: { 
        id: string; 
        checkoutUrl: string; 
        totalQuantity: number;
      } 
    } 
  };
  const data = await shopifyFetch<MutationResult>(query, variables);
  return data?.cartLinesAdd?.cart || null;
}

// Fetch existing cart
export async function fetchCart(cartId: string) {
  const query = `
    query getCart($cartId: ID!) {
      cart(id: $cartId) {
        id
        checkoutUrl
        totalQuantity
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
              }
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  priceV2 {
                    amount
                    currencyCode
                  }
                  product {
                    title
                    images(first: 1) {
                      edges {
                        node {
                          src
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;
  type QueryResult = { 
    cart?: { 
      id: string; 
      checkoutUrl: string; 
      totalQuantity: number;
      cost: {
        totalAmount: { amount: string; currencyCode: string };
        subtotalAmount: { amount: string; currencyCode: string };
      };
      lines: {
        edges: {
          node: {
            id: string;
            quantity: number;
            cost: { totalAmount: { amount: string; currencyCode: string } };
            merchandise: {
              id: string;
              title: string;
              priceV2: { amount: string; currencyCode: string };
              product: {
                title: string;
                images: { edges: { node: { src: string; altText?: string } }[] };
              };
            };
          };
        }[];
      };
    } 
  };
  const data = await shopifyFetch<QueryResult>(query, { cartId });
  return data?.cart || null;
}

// Create a customer (signup)
export async function customerCreate(email: string, password: string, firstName: string, lastName: string) {
  const query = `
    mutation customerCreate($input: CustomerCreateInput!) {
      customerCreate(input: $input) {
        customer {
          id
          email
          firstName
          lastName
        }
        customerUserErrors {
          field
          message
        }
      }
    }
  `;
  const variables = {
    input: { email, password, firstName, lastName }
  };
  type MutationResult = { customerCreate: { customer?: { id: string; email: string; firstName?: string; lastName?: string }, customerUserErrors: { field?: string[]; message: string }[] } };
  const data = await shopifyFetch<MutationResult>(query, variables);
  return data?.customerCreate;
}

// Login (get access token)
export async function customerLogin(email: string, password: string) {
  const query = `
    mutation login($input: CustomerAccessTokenCreateInput!) {
      customerAccessTokenCreate(input: $input) {
        customerAccessToken {
          accessToken
          expiresAt
        }
        customerUserErrors {
          field
          message
        }
      }
    }
  `;
  const variables = {
    input: { email, password }
  };
  type MutationResult = { customerAccessTokenCreate: { customerAccessToken?: { accessToken: string; expiresAt: string }, customerUserErrors: { field?: string[]; message: string }[] } };
  const data = await shopifyFetch<MutationResult>(query, variables);
  return data?.customerAccessTokenCreate;
}

// Fetch customer info
export async function fetchCustomer(accessToken: string) {
  const query = `
    query getCustomer($token: String!) {
      customer(customerAccessToken: $token) {
        id
        email
        firstName
        lastName
        orders(first: 5) {
          edges {
            node {
              id
              name
              totalPriceV2 {
                amount
                currencyCode
              }
            }
          }
        }
      }
    }
  `;
  type QueryResult = { customer?: { id: string; email: string; firstName?: string; lastName?: string } };
  const data = await shopifyFetch<QueryResult>(query, { token: accessToken });
  return data?.customer ?? null;
}

// Update cart line quantity
export async function updateCartLine(cartId: string, lineId: string, quantity: number) {
  const query = `
    mutation updateCartLine($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          id
          checkoutUrl
          totalQuantity
          cost {
            totalAmount {
              amount
              currencyCode
            }
            subtotalAmount {
              amount
              currencyCode
            }
          }
          lines(first: 10) {
            edges {
              node {
                id
                quantity
                cost {
                  totalAmount {
                    amount
                    currencyCode
                  }
                }
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    priceV2 {
                      amount
                      currencyCode
                    }
                    product {
                      title
                      images(first: 1) {
                        edges {
                          node {
                            src
                            altText
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const variables = {
    cartId,
    lines: [{ id: lineId, quantity }],
  };

  type MutationResult = { 
    cartLinesUpdate?: { 
      cart?: { 
        id: string; 
        checkoutUrl: string; 
        totalQuantity: number;
        cost: {
          totalAmount: { amount: string; currencyCode: string };
          subtotalAmount: { amount: string; currencyCode: string };
        };
        lines: {
          edges: {
            node: {
              id: string;
              quantity: number;
              cost: { totalAmount: { amount: string; currencyCode: string } };
              merchandise: {
                id: string;
                title: string;
                priceV2: { amount: string; currencyCode: string };
                product: {
                  title: string;
                  images: { edges: { node: { src: string; altText?: string } }[] };
                };
              };
            };
          }[];
        };
      } 
    } 
  };
  const data = await shopifyFetch<MutationResult>(query, variables);
  return data?.cartLinesUpdate?.cart || null;
}

// Remove cart line
export async function removeCartLine(cartId: string, lineId: string) {
  const query = `
    mutation removeCartLine($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          id
          checkoutUrl
          totalQuantity
          cost {
            totalAmount {
              amount
              currencyCode
            }
            subtotalAmount {
              amount
              currencyCode
            }
          }
          lines(first: 10) {
            edges {
              node {
                id
                quantity
                cost {
                  totalAmount {
                    amount
                    currencyCode
                  }
                }
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    priceV2 {
                      amount
                      currencyCode
                    }
                    product {
                      title
                      images(first: 1) {
                        edges {
                          node {
                            src
                            altText
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const variables = {
    cartId,
    lineIds: [lineId],
  };

  type MutationResult = { 
    cartLinesRemove?: { 
      cart?: { 
        id: string; 
        checkoutUrl: string; 
        totalQuantity: number;
        cost: {
          totalAmount: { amount: string; currencyCode: string };
          subtotalAmount: { amount: string; currencyCode: string };
        };
        lines: {
          edges: {
            node: {
              id: string;
              quantity: number;
              cost: { totalAmount: { amount: string; currencyCode: string } };
              merchandise: {
                id: string;
                title: string;
                priceV2: { amount: string; currencyCode: string };
                product: {
                  title: string;
                  images: { edges: { node: { src: string; altText?: string } }[] };
                };
              };
            };
          }[];
        };
      } 
    } 
  };
  const data = await shopifyFetch<MutationResult>(query, variables);
  return data?.cartLinesRemove?.cart || null;
}


